// Module 2 CRM — Contact Photo Auto-Suggest Enrichment (BL-150 / BL-193 Gate 3).
//
// Baseline enrichment provider: Gravatar by email MD5 hash.
// Additional enrichment sources: Unavatar API, Clearbit Logo API, UI-Avatars.
// Extensible via environment: REACT_APP_PHOTO_ENRICHMENT_API_URL & REACT_APP_PHOTO_ENRICHMENT_API_KEY.
//
// NOTE: All suggestions are SUGGEST-AND-CONFIRM only. They are NEVER auto-applied to the
// contact record without explicit staff confirmation.

/**
 * Pure JavaScript MD5 hash calculation for Gravatar lookup.
 * @param {string} str - Input string (e.g. trimmed lowercase email)
 * @returns {string} 32-character hexadecimal MD5 hash
 */
export function md5(str) {
    if (!str) return '';
    const string = String(str);

    function rotateLeft(lValue, iShiftBits) {
        return (lValue << iShiftBits) | (lValue >>> (32 - iShiftBits));
    }

    function addUnsigned(lX, lY) {
        const lX8 = (lX & 0x80000000);
        const lY8 = (lY & 0x80000000);
        const lX4 = (lX & 0x40000000);
        const lY4 = (lY & 0x40000000);
        const lResult = (lX & 0x3FFFFFFF) + (lY & 0x3FFFFFFF);
        if (lX4 & lY4) return (lResult ^ 0x80000000 ^ lX8 ^ lY8);
        if (lX4 | lY4) {
            if (lResult & 0x40000000) return (lResult ^ 0xC0000000 ^ lX8 ^ lY8);
            else return (lResult ^ 0x40000000 ^ lX8 ^ lY8);
        } else return (lResult ^ lX8 ^ lY8);
    }

    function F(x, y, z) { return (x & y) | ((~x) & z); }
    function G(x, y, z) { return (x & z) | (y & (~z)); }
    function H(x, y, z) { return (x ^ y ^ z); }
    function I(x, y, z) { return (y ^ (x | (~z))); }

    function FF(a, b, c, d, x, s, ac) {
        a = addUnsigned(a, addUnsigned(addUnsigned(F(b, c, d), x), ac));
        return addUnsigned(rotateLeft(a, s), b);
    }
    function GG(a, b, c, d, x, s, ac) {
        a = addUnsigned(a, addUnsigned(addUnsigned(G(b, c, d), x), ac));
        return addUnsigned(rotateLeft(a, s), b);
    }
    function HH(a, b, c, d, x, s, ac) {
        a = addUnsigned(a, addUnsigned(addUnsigned(H(b, c, d), x), ac));
        return addUnsigned(rotateLeft(a, s), b);
    }
    function II(a, b, c, d, x, s, ac) {
        a = addUnsigned(a, addUnsigned(addUnsigned(I(b, c, d), x), ac));
        return addUnsigned(rotateLeft(a, s), b);
    }

    const utf8Bytes = [];
    for (let i = 0; i < string.length; i++) {
        let code = string.charCodeAt(i);
        if (code < 0x80) {
            utf8Bytes.push(code);
        } else if (code < 0x800) {
            utf8Bytes.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
        } else if (code < 0xd800 || code >= 0xe000) {
            utf8Bytes.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
        } else {
            i++;
            code = 0x10000 + (((code & 0x3ff) << 10) | (string.charCodeAt(i) & 0x3ff));
            utf8Bytes.push(0xf0 | (code >> 18), 0x80 | ((code >> 12) & 0x3f), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
        }
    }

    const byteLen = utf8Bytes.length;
    const nWords = Math.ceil((byteLen + 9) / 64) * 16;
    const words = new Array(nWords).fill(0);
    for (let j = 0; j < byteLen; j++) {
        words[j >> 2] |= utf8Bytes[j] << ((j % 4) * 8);
    }
    words[byteLen >> 2] |= 0x80 << ((byteLen % 4) * 8);
    words[nWords - 2] = byteLen * 8;

    function wordToHex(lValue) {
        let WordToHexValue = '', WordToHexValue_temp = '', lByte, lCount;
        for (lCount = 0; lCount <= 3; lCount++) {
            lByte = (lValue >>> (lCount * 8)) & 255;
            WordToHexValue_temp = '0' + lByte.toString(16);
            WordToHexValue = WordToHexValue + WordToHexValue_temp.substr(WordToHexValue_temp.length - 2, 2);
        }
        return WordToHexValue;
    }

    let a = 0x67452301, b = 0xEFCDAB89, c = 0x98BADCFE, d = 0x10325476;
    const S11 = 7, S12 = 12, S13 = 17, S14 = 22;
    const S21 = 5, S22 = 9, S23 = 14, S24 = 20;
    const S31 = 4, S32 = 11, S33 = 16, S34 = 23;
    const S41 = 6, S42 = 10, S43 = 15, S44 = 21;

    for (let k = 0; k < words.length; k += 16) {
        const AA = a, BB = b, CC = c, DD = d;
        a = FF(a, b, c, d, words[k + 0], S11, 0xD76AA478);
        d = FF(d, a, b, c, words[k + 1], S12, 0xE8C7B756);
        c = FF(c, d, a, b, words[k + 2], S13, 0x242070DB);
        b = FF(b, c, d, a, words[k + 3], S14, 0xC1BDCEEE);
        a = FF(a, b, c, d, words[k + 4], S11, 0xF57C0FAF);
        d = FF(d, a, b, c, words[k + 5], S12, 0x4787C62A);
        c = FF(c, d, a, b, words[k + 6], S13, 0xA8304613);
        b = FF(b, c, d, a, words[k + 7], S14, 0xFD469501);
        a = FF(a, b, c, d, words[k + 8], S11, 0x698098D8);
        d = FF(d, a, b, c, words[k + 9], S12, 0x8B44F7AF);
        c = FF(c, d, a, b, words[k + 10], S13, 0xFFFF5BB1);
        b = FF(b, c, d, a, words[k + 11], S14, 0x895CD7BE);
        a = FF(a, b, c, d, words[k + 12], S11, 0x6B901122);
        d = FF(d, a, b, c, words[k + 13], S12, 0xFD987193);
        c = FF(c, d, a, b, words[k + 14], S13, 0xA679438E);
        b = FF(b, c, d, a, words[k + 15], S14, 0x49B40821);

        a = GG(a, b, c, d, words[k + 1], S21, 0xF61E2562);
        d = GG(d, a, b, c, words[k + 6], S22, 0xC040B340);
        c = GG(c, d, a, b, words[k + 11], S23, 0x265E5A51);
        b = GG(b, c, d, a, words[k + 0], S24, 0xE9B6C7AA);
        a = GG(a, b, c, d, words[k + 5], S21, 0xD62F105D);
        d = GG(d, a, b, c, words[k + 10], S22, 0x2441453);
        c = GG(c, d, a, b, words[k + 15], S23, 0xD8A1E681);
        b = GG(b, c, d, a, words[k + 4], S24, 0xE7D3FBC8);
        a = GG(a, b, c, d, words[k + 9], S21, 0x21E1CDE6);
        d = GG(d, a, b, c, words[k + 14], S22, 0xC33707D6);
        c = GG(c, d, a, b, words[k + 3], S23, 0xF4D50D87);
        b = GG(b, c, d, a, words[k + 8], S24, 0x455A14ED);
        a = GG(a, b, c, d, words[k + 13], S21, 0xA9E3E905);
        d = GG(d, a, b, c, words[k + 2], S22, 0xFCEFA3F8);
        c = GG(c, d, a, b, words[k + 7], S23, 0x676F02D9);
        b = GG(b, c, d, a, words[k + 12], S24, 0x8D2A4C8A);

        a = HH(a, b, c, d, words[k + 5], S31, 0xFFFA3942);
        d = HH(d, a, b, c, words[k + 8], S32, 0x8771F681);
        c = HH(c, d, a, b, words[k + 11], S33, 0x6D9D6122);
        b = HH(b, c, d, a, words[k + 14], S34, 0xFDE5380C);
        a = HH(a, b, c, d, words[k + 1], S31, 0xA4BEEA44);
        d = HH(d, a, b, c, words[k + 4], S32, 0x4BDECFA9);
        c = HH(c, d, a, b, words[k + 7], S33, 0xF6BB4B60);
        b = HH(b, c, d, a, words[k + 10], S34, 0xBEBFBC70);
        a = HH(a, b, c, d, words[k + 13], S31, 0x289B7EC6);
        d = HH(d, a, b, c, words[k + 0], S32, 0xEAA127FA);
        c = HH(c, d, a, b, words[k + 3], S33, 0xD4EF3085);
        b = HH(b, c, d, a, words[k + 6], S34, 0x4881D05);
        a = HH(a, b, c, d, words[k + 9], S31, 0xD9D4D039);
        d = HH(d, a, b, c, words[k + 12], S32, 0xE6DB99E5);
        c = HH(c, d, a, b, words[k + 15], S33, 0x1FA27CF8);
        b = HH(b, c, d, a, words[k + 2], S34, 0xC4AC5665);

        a = II(a, b, c, d, words[k + 0], S41, 0xF4292244);
        d = II(d, a, b, c, words[k + 7], S42, 0x432AFF97);
        c = II(c, d, a, b, words[k + 14], S43, 0xAB9423A7);
        b = II(b, c, d, a, words[k + 5], S44, 0xFC93A039);
        a = II(a, b, c, d, words[k + 12], S41, 0x655B59C3);
        d = II(d, a, b, c, words[k + 3], S42, 0x8F0CCC92);
        c = II(c, d, a, b, words[k + 10], S43, 0xFFEFF47D);
        b = II(b, c, d, a, words[k + 1], S44, 0x85845DD1);
        a = II(a, b, c, d, words[k + 8], S41, 0x6FA87E4F);
        d = II(d, a, b, c, words[k + 15], S42, 0xFE2CE6E0);
        c = II(c, d, a, b, words[k + 6], S43, 0xA3014314);
        b = II(b, c, d, a, words[k + 13], S44, 0x4E0811A1);
        a = II(a, b, c, d, words[k + 4], S41, 0xF7537E82);
        d = II(d, a, b, c, words[k + 11], S42, 0xBD3AF235);
        c = II(c, d, a, b, words[k + 2], S43, 0x2AD7D2BB);
        b = II(b, c, d, a, words[k + 9], S44, 0xEB86D391);

        a = addUnsigned(a, AA);
        b = addUnsigned(b, BB);
        c = addUnsigned(c, CC);
        d = addUnsigned(d, DD);
    }

    return (wordToHex(a) + wordToHex(b) + wordToHex(c) + wordToHex(d)).toLowerCase();
}

/**
 * Generate Gravatar URL for an email address.
 * @param {string} email
 * @param {number} [size=200]
 * @param {string} [defaultParam='404'] - '404' returns HTTP 404 if no Gravatar set
 * @returns {string} Gravatar image URL
 */
export function getGravatarUrl(email, size = 200, defaultParam = '404') {
    if (!email) return '';
    const cleanEmail = String(email).trim().toLowerCase();
    const hash = md5(cleanEmail);
    return `https://www.gravatar.com/avatar/${hash}?s=${size}&d=${encodeURIComponent(defaultParam)}`;
}

const GENERIC_WEBMAIL_DOMAINS = new Set([
    'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'live.com',
    'icloud.com', 'me.com', 'aol.com', 'protonmail.com', 'zoho.com', 'gmx.com'
]);

/**
 * Extracts company domain from email address if not a generic webmail provider.
 * @param {string} email
 * @returns {string|null} Domain name or null
 */
export function extractCompanyDomain(email) {
    if (!email || typeof email !== 'string') return null;
    const parts = email.trim().toLowerCase().split('@');
    if (parts.length !== 2) return null;
    const domain = parts[1];
    if (GENERIC_WEBMAIL_DOMAINS.has(domain)) return null;
    return domain;
}

/**
 * Generate photo suggestions from Gravatar and secondary enrichment APIs.
 * Note: These are suggestions ONLY and must be explicitly confirmed by staff.
 *
 * @param {Object} options
 * @param {string} [options.email]
 * @param {string} [options.name]
 * @param {string} [options.accountName]
 * @param {string} [options.customApiUrl] - Optional custom enrichment API endpoint
 * @param {string} [options.customApiKey] - Optional custom API key
 * @returns {Array<{id: string, source: string, sourceType: string, url: string, badge: string, description: string}>}
 */
export function fetchPhotoSuggestions({ email = '', name = '', accountName = '', customApiUrl = '', customApiKey = '' } = {}) {
    const suggestions = [];
    const cleanEmail = (email || '').trim();

    if (cleanEmail) {
        // Source 1: Gravatar (baseline enrichment source per BL-150)
        const gravatarUrl = getGravatarUrl(cleanEmail, 200, '404');
        suggestions.push({
            id: 'gravatar',
            source: 'Gravatar',
            sourceType: 'gravatar',
            url: gravatarUrl,
            badge: 'Gravatar',
            description: `Baseline avatar from Gravatar (${cleanEmail})`
        });

        // Source 2: Enrichment API (Unavatar or configured custom endpoint)
        const configuredApi = customApiUrl || (typeof process !== 'undefined' && process.env?.REACT_APP_PHOTO_ENRICHMENT_API_URL);
        const configuredKey = customApiKey || (typeof process !== 'undefined' && process.env?.REACT_APP_PHOTO_ENRICHMENT_API_KEY);

        let enrichmentUrl = '';
        if (configuredApi) {
            const separator = configuredApi.includes('?') ? '&' : '?';
            enrichmentUrl = `${configuredApi}${separator}email=${encodeURIComponent(cleanEmail)}`;
            // Note: If custom API requires auth, configure a CORS-enabled proxy endpoint or header handler.
        } else {
            // Unavatar as multi-provider enrichment fallback
            enrichmentUrl = `https://unavatar.io/${encodeURIComponent(cleanEmail)}?fallback=false`;
        }

        suggestions.push({
            id: 'enrichment_api',
            source: configuredApi ? 'Enrichment API (Custom)' : 'Enrichment API (Unavatar)',
            sourceType: 'enrichment_api',
            url: enrichmentUrl,
            badge: 'Enrichment API',
            description: 'Multi-provider avatar aggregation'
        });

        // Source 3: Company Logo (Clearbit) if company domain is present
        const domain = extractCompanyDomain(cleanEmail);
        if (domain) {
            suggestions.push({
                id: 'company_logo',
                source: `Company Logo (${domain})`,
                sourceType: 'company_logo',
                url: `https://logo.clearbit.com/${domain}`,
                badge: 'Company Logo',
                description: `Brand logo from ${domain}`
            });
        }
    }

    // Source 4: Stylized Initials Avatar (UI-Avatars) as generated option
    const displayName = (name || accountName || cleanEmail || 'Contact').trim();
    if (displayName) {
        const uiAvatarUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=0D9488&color=ffffff&size=200&bold=true`;
        suggestions.push({
            id: 'ui_avatar',
            source: 'UI Avatars',
            sourceType: 'ui_avatar',
            url: uiAvatarUrl,
            badge: 'Initials Badge',
            description: `Generated avatar for ${displayName}`
        });
    }

    return suggestions;
}

/**
 * Helper for manual photo file upload.
 * If Supabase storage is configured, uploads to 'avatars' bucket.
 * Fallback converts file to Base64 Data URL.
 *
 * @param {File} file
 * @param {Object} [supabaseClient]
 * @param {string} [contactId]
 * @returns {Promise<{url: string, isDataUrl: boolean}>}
 */
export async function uploadContactPhoto(file, supabaseClient = null, contactId = null) {
    if (!file) throw new Error('No file provided for upload.');

    // 1. Try Supabase Storage if client and bucket available
    if (supabaseClient?.storage) {
        try {
            const ext = file.name.split('.').pop() || 'png';
            const fileName = `contact_${contactId || 'temp'}_${Date.now()}.${ext}`;
            const path = `avatars/${fileName}`;

            const { data, error } = await supabaseClient.storage
                .from('avatars')
                .upload(path, file, { upsert: true });

            if (!error && data?.path) {
                const { data: pubData } = supabaseClient.storage
                    .from('avatars')
                    .getPublicUrl(data.path);
                if (pubData?.publicUrl) {
                    return { url: pubData.publicUrl, isDataUrl: false };
                }
            }
        } catch (e) {
            // Storage bucket may not be created; fall back to Data URL
        }
    }

    // 2. Fallback: Convert to Base64 Data URL
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve({ url: reader.result, isDataUrl: true });
        reader.onerror = (err) => reject(err);
        reader.readAsDataURL(file);
    });
}
